/**
 * @file event-definition.ts
 * @module EventDefinition
 * @description Mapbox GL stilu slāņu definīcijas plānotajiem notikumiem (ceļu darbiem)
 *              iegultajā kartē. Konfigurē gan līniju, gan punktu stilus ar krāsu
 *              kodētām kategorijām un no tālummaiņas atkarīgu redzamību.
 */

import type { LayerSpecification } from "maplibre-gl";
import { EMBED_ORIGIN } from "./uvis-definition";

/** Unplanned event point layer: road blocked (icon: closed road sign) */
export const CLOSED_ROAD_POINT: LayerSpecification = {
	id: "closed-road-point",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_notplannedevent_public",
	type: "symbol",
	filter: ["all", ["==", ["geometry-type"], "Point"], ["==", ["get", "restrictions"], "['roadBlocked']"]],
	layout: {
		"icon-image": "pins-slegts-celjsh",
		"icon-rotation-alignment": "viewport",
		"icon-size": 1,
		"icon-offset": [0, -20],
		"icon-allow-overlap": true,
		"icon-ignore-placement": true,
	},
};

export const CLOSED_ROAD_POINT_ALT: LayerSpecification = {
	id: "closed-road-point-alt",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_plannedevent_public",
	type: "symbol",
	filter: ["all", ["==", ["geometry-type"], "Point"], ["==", ["get", "restrictions"], "['roadBlocked']"], ["==", ["get", "status"], "ACTIVE"]],
	layout: {
		"icon-image": "pins-slegts-celjsh",
		"icon-rotation-alignment": "viewport",
		"icon-size": 1,
		"icon-offset": [0, -20],
		"icon-allow-overlap": true,
		"icon-ignore-placement": true,
	},
};

export const CLOSED_ROAD_LINE_ALT: LayerSpecification = {
	id: "closed-road-line-alt",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_plannedevent_public",
	type: "line",
	filter: [
		"all",
		["any", ["==", ["geometry-type"], "MultiLineString"], ["==", ["geometry-type"], "LineString"]],
		["==", ["get", "restrictions"], "['roadBlocked']"],
		["==", ["get", "status"], "ACTIVE"],
	],
	layout: {
		"line-join": "round",
		"line-cap": "round",
	},
	paint: {
		"line-color": ["case", ["boolean", ["feature-state", "highlighted"], false], "#00ccf5", "#545454"],
		"line-width": ["interpolate", ["linear"], ["zoom"], 10, 5, 16, 10],
	},
};

export const CLOSED_ROAD_LINE: LayerSpecification = {
	id: "closed-road-line",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_notplannedevent_public",
	type: "line",
	filter: ["all", ["any", ["==", ["geometry-type"], "MultiLineString"], ["==", ["geometry-type"], "LineString"]], ["==", ["get", "restrictions"], "['roadBlocked']"]],
	layout: {
		"line-join": "round",
		"line-cap": "round",
	},
	paint: {
		"line-color": ["case", ["boolean", ["feature-state", "highlighted"], false], "#00ccf5", "#545454"],
		"line-width": ["interpolate", ["linear"], ["zoom"], 10, 5, 16, 10],
	},
};

export const ACCIDENT_ROAD_LINE: LayerSpecification = {
	id: "accident-road-line",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_notplannedevent_public",
	type: "line",
	filter: [
		"all",
		["any", ["==", ["geometry-type"], "MultiLineString"], ["==", ["geometry-type"], "LineString"]],
		[
			"any",
			["==", ["get", "datex_subclass"], "accidentType"],
			["all", ["==", ["get", "datex_subclass"], "obstructionType"], ["==", ["get", "datex_enum"], "rescueAndRecoveryWork"]],
			["all", ["==", ["get", "datex_subclass"], "obstructionType"], ["==", ["get", "datex_enum"], "unprotectedAccidentArea"]],
		],
		["!=", ["get", "restrictions"], "['roadBlocked']"],
	],
	layout: {
		"line-join": "round",
		"line-cap": "round",
	},
	paint: {
		"line-color": ["case", ["boolean", ["feature-state", "highlighted"], false], "#00ccf5", "#545454"],
		"line-width": ["interpolate", ["linear"], ["zoom"], 10, 5, 16, 10],
	},
};

export const ACCIDENT_ROAD_POINT: LayerSpecification = {
	id: "accident-road-point",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_notplannedevent_public",
	type: "symbol",
	filter: [
		"all",
		["==", ["geometry-type"], "Point"],
		[
			"any",
			["==", ["get", "datex_subclass"], "accidentType"],
			["all", ["==", ["get", "datex_subclass"], "obstructionType"], ["==", ["get", "datex_enum"], "rescueAndRecoveryWork"]],
			["all", ["==", ["get", "datex_subclass"], "obstructionType"], ["==", ["get", "datex_enum"], "unprotectedAccidentArea"]],
		],
		["!=", ["get", "restrictions"], "['roadBlocked']"],
	],
	layout: {
		"icon-image": "pins-avarija",
		"icon-rotation-alignment": "viewport",
		"icon-size": 1,
		"icon-offset": [0, -20],
		"icon-allow-overlap": true,
		"icon-ignore-placement": true,
	},
};

export const OTHER_ROAD_LINE: LayerSpecification = {
	id: "other-road-line",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_notplannedevent_public",
	type: "line",
	filter: [
		"all",
		["any", ["==", ["geometry-type"], "MultiLineString"], ["==", ["geometry-type"], "LineString"]],
		[
			"any",
			["==", ["get", "datex_subclass"], "nonWeatherRelatedRoadConditionType"],
			["==", ["get", "datex_subclass"], "animalPresenceType"],
			["==", ["get", "datex_subclass"], "infrastructureDamageType"],
			["==", ["get", "datex_subclass"], "vehicleObstructionType"],
			["==", ["get", "datex_subclass"], "environmentalObstructionType"],
			["all", ["==", ["get", "datex_subclass"], "obstructionType"], ["!=", ["get", "datex_enum"], "unprotectedAccidentArea"]],
		],
		["!=", ["get", "restrictions"], "['roadBlocked']"],
	],
	layout: {
		"line-join": "round",
		"line-cap": "round",
	},
	paint: {
		"line-color": ["case", ["boolean", ["feature-state", "highlighted"], false], "#00ccf5", "#545454"],
		"line-width": ["interpolate", ["linear"], ["zoom"], 10, 5, 16, 10],
	},
};

export const OTHER_ROAD_POINT: LayerSpecification = {
	id: "other-road-point",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_notplannedevent_public",
	type: "symbol",
	filter: [
		"all",
		["==", ["geometry-type"], "Point"],
		[
			"any",
			["==", ["get", "datex_subclass"], "nonWeatherRelatedRoadConditionType"],
			["==", ["get", "datex_subclass"], "animalPresenceType"],
			["==", ["get", "datex_subclass"], "infrastructureDamageType"],
			["==", ["get", "datex_subclass"], "vehicleObstructionType"],
			["==", ["get", "datex_subclass"], "environmentalObstructionType"],
			["all", ["==", ["get", "datex_subclass"], "obstructionType"], ["!=", ["get", "datex_enum"], "unprotectedAccidentArea"]],
		],
		["!=", ["get", "restrictions"], "['roadBlocked']"],
	],
	layout: {
		"icon-image": "pins-izsaukuma-zime",
		"icon-rotation-alignment": "viewport",
		"icon-size": 1,
		"icon-offset": [0, -20],
		"icon-allow-overlap": true,
		"icon-ignore-placement": true,
	},
};

export const OTHER_ROAD_POINT_ALT: LayerSpecification = {
	id: "other-road-point-alt",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_plannedevent_public",
	type: "symbol",
	filter: [
		"all",
		["==", ["geometry-type"], "Point"],
		["==", ["get", "datex_subclass"], "massEvent"],
		["!=", ["get", "restrictions"], "['roadBlocked']"],
		["==", ["get", "status"], "ACTIVE"],
	],
	layout: {
		"icon-image": "pins-izsaukuma-zime",
		"icon-rotation-alignment": "viewport",
		"icon-size": 1,
		"icon-offset": [0, -20],
		"icon-allow-overlap": true,
		"icon-ignore-placement": true,
	},
};

export const OTHER_ROAD_LINE_ALT: LayerSpecification = {
	id: "other-road-line-alt",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_plannedevent_public",
	type: "line",
	filter: [
		"all",
		["any", ["==", ["geometry-type"], "MultiLineString"], ["==", ["geometry-type"], "LineString"]],
		["==", ["get", "datex_subclass"], "massEvent"],
		["!=", ["get", "restrictions"], "['roadBlocked']"],
		["==", ["get", "status"], "ACTIVE"],
	],
	layout: {
		"line-join": "round",
		"line-cap": "round",
	},
	paint: {
		"line-color": ["case", ["boolean", ["feature-state", "highlighted"], false], "#00ccf5", "#545454"],
		"line-width": ["interpolate", ["linear"], ["zoom"], 10, 5, 16, 10],
	},
};

export const ROAD_POINT_ORANGE: LayerSpecification = {
	id: "road-work-point-orange",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_plannedevent_public",
	type: "symbol",
	filter: [
		"all",
		["==", ["geometry-type"], "Point"],
		["==", ["get", "event_subclass"], EMBED_ORIGIN === "map-test.lvceli.lv" ? "constructionWork" : "constructionWorkType"],
		["==", ["get", "event_enum"], "constructionWork"],
		[
			"all",
			["!", ["==", ["get", "construction_paused"], true]],
			["any", ["!", ["has", "notes"]], ["!", ["in", "tehnoloģiskais pārtraukums", ["downcase", ["get", "notes"]]]]],
		],
		["!", ["==", ["get", "status"], "PLANNED"]],
		["!", ["==", ["get", "restrictions"], "['roadBlocked']"]],
	],
	layout: {
		"icon-image": "orange_roadwork",
		"icon-rotation-alignment": "viewport",
		"icon-size": 1,
		"icon-offset": [0, -20],
		"icon-allow-overlap": true,
		"icon-ignore-placement": true,
	},
};

export const ROAD_POINT_CLOSED: LayerSpecification = {
	id: "road-work-point-closed",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_plannedevent_public",
	type: "symbol",
	filter: [
		"all",
		["==", ["geometry-type"], "Point"],
		["==", ["get", "event_subclass"], EMBED_ORIGIN === "map-test.lvceli.lv" ? "constructionWork" : "constructionWorkType"],
		["==", ["get", "event_enum"], "constructionWork"],
		[
			"all",
			["!", ["==", ["get", "construction_paused"], true]],
			["any", ["!", ["has", "notes"]], ["!", ["in", "tehnoloģiskais pārtraukums", ["downcase", ["get", "notes"]]]]],
		],
		["!", ["==", ["get", "status"], "PLANNED"]],
		["==", ["get", "restrictions"], "['roadBlocked']"],
	],
	layout: {
		"icon-image": "pins-slegts-celjsh",
		"icon-rotation-alignment": "viewport",
		"icon-size": 1,
		"icon-offset": [0, -20],
		"icon-allow-overlap": true,
		"icon-ignore-placement": true,
	},
};

export const ROAD_LINE_ORANGE: LayerSpecification = {
	id: "road-work-line-orange",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_plannedevent_public",
	type: "line",
	filter: [
		"all",
		["any", ["==", ["geometry-type"], "MultiLineString"], ["==", ["geometry-type"], "LineString"]],
		["==", ["get", "event_subclass"], EMBED_ORIGIN === "map-test.lvceli.lv" ? "constructionWork" : "constructionWorkType"],
		["==", ["get", "event_enum"], "constructionWork"],
		[
			"all",
			["!", ["==", ["get", "construction_paused"], true]],
			["any", ["!", ["has", "notes"]], ["!", ["in", "tehnoloģiskais pārtraukums", ["downcase", ["get", "notes"]]]]],
		],
		["!", ["==", ["get", "status"], "PLANNED"]],
	],
	paint: {
		"line-color": "#545454",
		"line-width": ["interpolate", ["linear"], ["zoom"], 10, 5, 16, 10],
	},
};

export const ROAD_POINT_BLACK: LayerSpecification = {
	id: "road-work-point-black",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_plannedevent_public",
	type: "symbol",
	filter: [
		"all",
		["==", ["geometry-type"], "Point"],
		["==", ["get", "event_subclass"], EMBED_ORIGIN === "map-test.lvceli.lv" ? "constructionWork" : "constructionWorkType"],
		["==", ["get", "event_enum"], "constructionWork"],
		["any", ["all", ["has", "notes"], ["in", "tehnoloģiskais pārtraukums", ["downcase", ["get", "notes"]]]], ["boolean", ["get", "construction_paused"]]],
	],
	layout: {
		"icon-image": "black_summer",
		"icon-rotation-alignment": "viewport",
		"icon-size": 1,
		"icon-offset": [0, -20],
		"icon-allow-overlap": true,
		"icon-ignore-placement": true,
	},
};

export const ROAD_LINE_BLACK: LayerSpecification = {
	id: "road-work-line-black",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_plannedevent_public",
	type: "line",
	filter: [
		"all",
		["any", ["==", ["geometry-type"], "MultiLineString"], ["==", ["geometry-type"], "LineString"]],
		["==", ["get", "event_subclass"], EMBED_ORIGIN === "map-test.lvceli.lv" ? "constructionWork" : "constructionWorkType"],
		["==", ["get", "event_enum"], "constructionWork"],
		["any", ["all", ["has", "notes"], ["in", "tehnoloģiskais pārtraukums", ["downcase", ["get", "notes"]]]], ["boolean", ["get", "construction_paused"]]],
	],
	paint: {
		"line-color": "#545454",
		"line-width": ["interpolate", ["linear"], ["zoom"], 10, 5, 16, 10],
	},
};

export const ROAD_POINT_BLUE_ALT: LayerSpecification = {
	id: "road-work-point-blue-alt",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_soconstructionplan_public",
	type: "symbol",
	filter: ["==", ["geometry-type"], "Point"],
	layout: {
		"icon-image": "blue_roadwork",
		"icon-rotation-alignment": "viewport",
		"icon-size": 1,
		"icon-offset": [0, -20],
		"icon-allow-overlap": true,
		"icon-ignore-placement": true,
	},
};

export const ROAD_LINE_BLUE_ALT: LayerSpecification = {
	id: "road-work-line-blue-alt",
	source: "pgtiles",
	"source-layer": "public.kafkamessages_soconstructionplan_public",
	type: "line",
	filter: ["all", ["any", ["==", ["geometry-type"], "MultiLineString"], ["==", ["geometry-type"], "LineString"]]],
	paint: {
		"line-color": "#545454",
		"line-width": ["interpolate", ["linear"], ["zoom"], 10, 5, 16, 10],
	},
};
